import type { ActivityEvent } from '@/shared/types/activityEvent';

type ResourceLinkResolver = (event: ActivityEvent) => string;

/**
 * Declarative route map from resource types to application URLs.
 * New domains register their resource types here.
 */
const RESOURCE_LINKS: Record<string, ResourceLinkResolver> = {
  // Workflow domain
  workflow_spot: (e) => e.context.program_id && e.context.team_id
    ? `/workflow/team-workflow/program/${e.context.program_id}/team/${e.context.team_id}`
    : '/workflow/team-workflow',
  hiring_spot: (e) => e.context.program_id && e.context.team_id
    ? `/workflow/team-workflow/program/${e.context.program_id}/team/${e.context.team_id}`
    : '/workflow/team-workflow',
  employee: (e) => e.context.program_id
    ? `/workflow/summary?program=${e.context.program_id}`
    : '/workflow/summary',
  team: (e) => e.context.program_id
    ? `/workflow/team-workflow/program/${e.context.program_id}/team/${e.subject.resource_id}`
    : '/workflow/team-workflow',
  workflow: (e) => `/workflow/summary?workflow=${e.subject.resource_id}`,
  // Goals domain
  csf_info: (e) => e.context.card_id
    ? `/goals/critical-success-factors?card=${e.context.card_id}`
    : '/goals/critical-success-factors',
  csf_status: (e) => e.context.card_id
    ? `/goals/critical-success-factors?card=${e.context.card_id}`
    : '/goals/critical-success-factors',
  company_initiative: () => '/goals/company-initiatives',
  portfolio: () => '/goals/department-initiatives',

  // CRM domain
  key_moment: (e) => e.context.company_id
    ? `/crm/companies/${e.context.company_id}`
    : '/crm/moments',
  contact: (e) => e.context.company_id
    ? `/crm/companies/${e.context.company_id}`
    : '/crm/people',
  company_review: (e) => e.context.company_id
    ? `/crm/companies/${e.context.company_id}`
    : '/crm',

  // Billing domain
  billing_cycle: () => '/billing/review',

  // Hiring domain
  confirmed_hire: () => '/hiring/placement',

  // Sales domain
  audit: () => '/sales/audits',
};

/**
 * Resolves the navigation link for an activity event based on its resource type.
 * Falls back to the domain's root page if no specific resolver is registered.
 */
export function getResourceLink(event: ActivityEvent): string {
  const resolver = RESOURCE_LINKS[event.subject.resource_type];
  return resolver?.(event) ?? `/${event.domain}`;
}
